import * as THREE from "three";
import { HqMaterials } from "./hqMaterials";
import { VfxLights } from "./vfxLights";
import { CloudShadows } from "./cloudShadows";
import { groundCommon, groundMap } from "./groundShader";
import {
  CliffEmissiveGlsl, CliffHazeGlsl, CliffMapGlsl, CliffNormalGlsl, cliffCommon, cliffLights, groundEmissive, groundHaze, groundLights, groundNormal,
  groundRoughness, groundSharedGlsl, windBandGlsl,
} from "./groundGlsl";
import type { Biome } from "../ui/chapters";
import type { SlopeMap } from "./groundSlope";
import { RoadSkin } from "./skins/roadSkin";
import { RoadPave } from "./groundPave";
import { appliedTier } from "./graphicsPrefs";
import { smoothstepN } from "./groundNoise";
import { GroundCliffs } from "./groundCliffs";
import { GroundGeometry } from "./groundGeometry";
import { GroundMaps, type Maps } from "./groundMaps";

/**
 * The ground's materials (groundMesh.ts's third layer, over groundCliffs.ts): the surface, gorge-wall and
 * chasm-floor shaders, what they share (haze, clouds, wind, lava glow), the grass palette and the texture-unit budget.
 */
export class GroundMaterials extends GroundCliffs {
  /**
   * Fragment texture units the surface shader needs before the optional ones: its six layer maps (dirt, cobble and
   * its normal, rock and its normal, the splat), plus the standard material's map, normal map, environment map and
   * the sun's shadow map. The optional ones, in the order they are granted: the ashlands' two emissive masks, the
   * slope map, the road's height map, the grass's height map.
   *
   * It was 14. The roughness maps were constants (grass 255, dirt/soil/sand 242, rock 232 — measured, not assumed)
   * and are uniforms now; the dirt parallax and its height map went (a worn shoulder does not need depth); and the
   * dirt normal map was bound but never read.
   */
  static readonly BaseSamplers = 10;
  /** The device's fragment texture-unit limit (16 on iPhone); SceneRig sets it from the renderer. */
  static maxSamplers = 16;
  /**
   * How far past a disc's edge (cells) and how high over the melt (units) its glow reaches. They were 1.5 and 1.7:
   * the ground round a basin sits 1.05 over the melt, so the rim took over a third of the glow's strength for a cell
   * and a half all round, and a road passing a river washed out to flat orange (audit 23-ash-slope). The walls, down
   * in the glow, keep nearly all of it; the rim keeps a warm edge and the cobbles keep their contrast.
   */
  static readonly LavaGlowReach = 1.0;
  static readonly LavaGlowRise = 1.4;

  protected splat: THREE.DataTexture | null = null;
  protected slope: SlopeMap | null = null;
  /** Lava basins as (x, z, radius, melt y), for the baked glow on the ground and cliffs (terrain F11). */
  protected lavaSpots: THREE.Vector4[] = [];
  /** The surface shader's splat uniform, so a resized splat texture can be swapped in. */
  protected splatUniform: { value: THREE.Texture | null } | null = null;

  /** 0..1 strength of the preview's warm lift; the level pulses it. */
  static readonly previewGlow = { value: 1 };

  /**
   * Grass colour per biome (terrain F1, lighting F1c). `base` is what the grass map's AVERAGE is mapped to — the
   * map's own light/dark and hue detail rides on top of it — and `dry`/`lush` are the two outer stops of the macro
   * hue ramp, as sRGB hex. A biome with no `base` keeps its map's own colour and only takes the ramp.
   *
   * The meadow was hue 128°, saturation 0.75 in game against the reference's 65° / 0.47. Three things stacked blue
   * on it; the one that lives here was `grassC *= vec3(0.86, 0.98, 0.80)`, which stripped red from a map that was
   * already a flat green (its blue channel's spread is 3/255). The base is now hue 85°, saturation 0.50 — 20% under
   * the map's 0.63 — and 11% brighter than before; the warm key moves it toward yellow in the lit areas, and the grade
   * (look.ts) no longer pushes it back. Measured target in a capture: lit grass hue 75–90°, s 0.5–0.6, v 0.55–0.62.
   */
  static readonly GrassPalette: Record<Biome, { base: number | null; ref: number; dry: number; lush: number; desat: number }> = {
    meadow: { base: 0x5d753b, ref: 0x8cbf4e, dry: 0xa9b25a, lush: 0x3f7f4a, desat: 0 },
    marsh: { base: null, ref: 0x7a8a5a, dry: 0x9a9a5c, lush: 0x3f7a6a, desat: 0.15 },
    ashlands: { base: null, ref: 0x807a74, dry: 0xa39c90, lush: 0x5c4c46, desat: 0 },
  };
  /**
   * How far a full swing of the macro ramp moves the grass toward `dry` or `lush`. The noise sits mostly within half
   * its range, so a typical patch moves ~18% — three times the old ±6% drift, which no capture could see.
   */
  static readonly RampReach = 0.36;
  /** The ground's share of the image-based light. The blue PMREM at 0.75 was a blue DIFFUSE fill on a rough surface. */
  static readonly EnvIntensity = 0.25;
  /** Roughness per layer, in place of the maps that measured as constants. */
  static readonly Roughness = { grass: 1.0, dirt: 0.95, rock: 0.91, road: 0.87 };
  /** Direction the wind blows across the map (normalised), shared with the prop sway and the tree gusts. */
  static readonly WindDir: readonly [number, number] = [0.8, 0.6];
  /** The glow the lava bakes onto the ground and walls round a basin (pushed past 1 for the bloom). */
  static readonly LavaGlow = new THREE.Color(1.45, 0.46, 0.09);
  /** Shared across the surface, the cliffs and the chasm floor so the look can keep all three in step with the fog. */
  protected static readonly haze = { value: new THREE.Color(0xa9c2de) };

  /** The ground shader's haze colour, so the look can keep it in step with the scene fog. */
  static hazeUniform: { value: THREE.Color } | null = null;
  /**
   * GLSL shared by the surface, the cliffs and the chasm floor: the rectangular map-edge haze and the lava glow
   * (groundGlsl.ts). Declares nothing the three.js chunks declare.
   */
  protected static readonly SharedGlsl = groundSharedGlsl(GroundMaterials.LavaGlowReach, GroundMaterials.LavaGlowRise);

  /** The wind band (terrain F7) as GLSL (groundGlsl.ts); `windBand` is the same field on the CPU. */
  static windGlsl(dx: number, dz: number): string { return windBandGlsl(dx, dz); }

  /** Uniforms and GLSL every ground-family shader shares: the haze box, the clouds, the wind, the lava glow. */
  protected sharedUniforms(): Record<string, THREE.IUniform> {
    const gw = this.grid.width, gh = this.grid.height;
    GroundMaterials.hazeUniform = GroundMaterials.haze;
    return {
      uBounds: { value: new THREE.Vector4(-0.5, -0.5, gw, gh) },
      uHaze: GroundMaterials.haze,
      uFog: { value: new THREE.Vector2(GroundGeometry.FogStart, GroundGeometry.FogFull) },
      // the wind and the lava pulse run on the shared material clock; the clouds on CloudShadows' own uniforms, the
      // very objects every prop material holds, so ground and props agree on the shade and on uCloudAmount (dusk)
      uWindTime: HqMaterials.time,
      ...CloudShadows.uniforms,
      uLava: { value: this.lavaSpots.length ? this.lavaSpots : [new THREE.Vector4()] },
      uLavaCol: { value: GroundMaterials.LavaGlow },
    };
  }

  protected surfaceMaterial(m: Maps): THREE.MeshStandardMaterial {
    // No roughness map: every ground set's roughness maps are flat constants (see `Roughness`), and a constant costs
    // a texture unit and a tap per pixel for nothing.
    const mat = new THREE.MeshStandardMaterial({ map: m.grass.alb, normalMap: m.grass.nrm, metalness: 0, roughness: 1 });
    mat.envMapIntensity = GroundMaterials.EnvIntensity;
    const emi = m.grass.emi ?? m.cobble.emi;
    // Texture units are the budget here: iOS Safari links a fragment shader against 16, and once this shader plus
    // the standard material's own came to 17 the program failed to link on the phone and the ground vanished. Pay
    // only for what the level uses, most important first.
    let units = GroundMaterials.BaseSamplers + (emi ? 2 : 0);
    const take = (want: boolean) => { if (!want || units + 1 > GroundMaterials.maxSamplers) return false; units++; return true; };
    const useSlope = take(true);
    const roadHgt = take(!!m.cobble.hgt);
    const grassHgt = take(!!m.grass.hgt);
    const lavaN = this.lavaSpots.length;
    const defs: Record<string, string> = {};
    if (emi) defs.GROUND_EMI = "";
    if (useSlope) defs.GROUND_SLOPE = "";
    if (roadHgt) defs.ROAD_HGT = "";
    if (grassHgt) defs.GRASS_HGT = "";
    if (lavaN) defs.GROUND_LAVA = String(lavaN);
    if (!appliedTier().groundNormalDual) defs.GROUND_NORMAL_SINGLE = "";   // the tier's, as of this level's build
    mat.defines = defs;
    // the program cache keys on onBeforeCompile's source, which is the same for every variant
    mat.customProgramCacheKey = () => `td-ground:${Object.keys(defs).sort().join(",")}:${lavaN}`;
    const pal = GroundMaterials.GrassPalette[GroundMaps.currentBiome] ?? GroundMaterials.GrassPalette.meadow;
    const lin = (hex: number) => new THREE.Color(hex);     // hex is sRGB; Color stores it linear
    const ref = lin(pal.ref);
    const ratio = (hex: number) => { const c = lin(hex); return new THREE.Vector3(c.r / ref.r, c.g / ref.g, c.b / ref.b); };
    const mean = m.grass.mean;
    const gain = pal.base === null || !mean ? new THREE.Vector3(1, 1, 1)
      : (() => { const b = lin(pal.base); return new THREE.Vector3(b.r / mean.r, b.g / mean.g, b.b / mean.b); })();
    const R = GroundMaterials.Roughness;
    const [wdx, wdz] = GroundMaterials.WindDir;
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.sharedUniforms());
      sh.uniforms.uDirt = { value: m.dirt.alb };
      sh.uniforms.uRoad = { value: m.cobble.alb }; sh.uniforms.uRoadN = { value: m.cobble.nrm };
      sh.uniforms.uRoadH = { value: m.cobble.hgt };
      sh.uniforms.uGrassH = { value: m.grass.hgt };
      sh.uniforms.uRock = { value: m.rock.alb }; sh.uniforms.uRockN = { value: m.rock.nrm };
      sh.uniforms.uEmiA = { value: m.grass.emi ?? emi };
      sh.uniforms.uEmiR = { value: m.cobble.emi ?? emi };
      sh.uniforms.uEmiOn = { value: emi ? 1 : 0 };
      sh.uniforms.uEmiColour = { value: new THREE.Color(2.4, 0.78, 0.16) };
      sh.uniforms.uRoadTint = RoadSkin.tint;
      Object.assign(sh.uniforms, RoadPave.uniforms);         // a road skin's paving (groundPave.ts)
      this.splatUniform = { value: this.splat };
      sh.uniforms.uSplat = this.splatUniform;
      sh.uniforms.uPreviewGlow = GroundMaterials.previewGlow;
      const slope = this.slope!;
      sh.uniforms.uSlope = { value: slope.texture };
      sh.uniforms.uSlopeBox = { value: new THREE.Vector4(slope.origin.x, slope.origin.y, slope.size.x, slope.size.y) };
      sh.uniforms.uGrassGain = { value: gain };
      sh.uniforms.uDryGain = { value: ratio(pal.dry) };
      sh.uniforms.uLushGain = { value: ratio(pal.lush) };
      sh.uniforms.uGrassDesat = { value: pal.desat };
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vWP;\nvarying vec3 vEye;\nvarying vec2 vUnwarped;")
        .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\n  vWP = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vEye = cameraPosition - vWP;\n  vUnwarped = uv * " + GroundGeometry.TexScale.toFixed(1) + ";");
      const S = GroundGeometry.TexScale;
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>\n${groundCommon(S, GroundMaterials.SharedGlsl, CloudShadows.glsl, GroundMaterials.windGlsl(wdx, wdz))}`)
        .replace("#include <map_fragment>", groundMap(GroundMaterials.RampReach))
        .replace("#include <emissivemap_fragment>", groundEmissive)
        .replace("#include <lights_fragment_end>", groundLights(CloudShadows.applyGlsl("vWP")))
        .replace("#include <opaque_fragment>", groundHaze)
        .replace("#include <roughnessmap_fragment>", groundRoughness(R))
        .replace("#include <normal_fragment_maps>", groundNormal);
      VfxLights.inject(sh);                                     // VFX light slots (vfxLights.ts): last, after every other replace
    };
    return mat;
  }

  protected cliffMaterial(m: Maps): THREE.MeshStandardMaterial {
    // Double-sided: faces are lofted per edge with windings that depend on the edge's direction. Nothing here is thin
    // enough for the back faces to show — now that the outer apron no longer gets a wall, and the haze covers the rest.
    // The purpose-made gorge wall when the biome ships one (fractured slabs in earth: it carries its own soil, so the
    // shader takes it as the base instead of building earth-with-outcrops out of the soil and rock maps).
    const gorge = m.gorge !== m.rock;
    const wall = gorge ? m.gorge : m.rock;
    const mat = new THREE.MeshStandardMaterial({ map: wall.alb, normalMap: wall.nrm, metalness: 0, roughness: GroundMaterials.Roughness.rock, side: THREE.DoubleSide });
    mat.envMapIntensity = GroundMaterials.EnvIntensity + 0.1;
    const lavaN = this.lavaSpots.length;
    mat.defines = {};
    if (lavaN) mat.defines.GROUND_LAVA = String(lavaN);
    if (gorge) mat.defines.CLIFF_GORGE = "";
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.sharedUniforms());
      sh.uniforms.uSoil = { value: m.soil.alb };
      sh.uniforms.uGrass = { value: m.grass.alb };
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nattribute float cliffTop;\nvarying vec3 vWP;\nvarying float vTop;")
        .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\n  vWP = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vTop = cliffTop;");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", cliffCommon(GroundMaterials.SharedGlsl, CloudShadows.glsl))
        .replace("#include <map_fragment>", CliffMapGlsl)
        .replace("#include <normal_fragment_maps>", CliffNormalGlsl)
        .replace("#include <emissivemap_fragment>", CliffEmissiveGlsl)
        .replace("#include <lights_fragment_end>", cliffLights(CloudShadows.applyGlsl("vWP")))
        .replace("#include <opaque_fragment>", CliffHazeGlsl);
    };
    mat.customProgramCacheKey = () => `td-cliff:${lavaN}:${gorge ? "g" : ""}`;
    return mat;
  }

  /** The chasm floor sits in the mist the walls fade into: darker and bluer than they are at their deepest. */
  protected floorFog(mat: THREE.MeshStandardMaterial): void {
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.sharedUniforms());
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vWP;")
        .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\n  vWP = (modelMatrix * vec4(transformed, 1.0)).xyz;");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>
          varying vec3 vWP;
          ${GroundMaterials.SharedGlsl}`)
        .replace("#include <opaque_fragment>", `#include <opaque_fragment>
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uHaze * vec3(0.42, 0.50, 0.66), 0.5);
          float fh = smoothstep(uFog.x, uFog.y, beyondMap(vWP.xz));
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uHaze, fh);`);
    };
    mat.customProgramCacheKey = () => "td-pitfloor";
  }
}

/**
 * The wind band of `GroundMesh.windGlsl`, on the CPU: 0 calm, up to 1 in a gust's crest. The same field drives the
 * grass colour, the prop sway and the tree gust clips, so all three agree about where the gust is.
 */
export function windBand(x: number, z: number, t: number): number {
  const [dx, dz] = GroundMaterials.WindDir;
  const along = x * dx + z * dz;
  const b = Math.sin(along * 0.8 - t * 1.6);
  const gate = 0.5 + 0.5 * Math.sin((x * -dz + z * dx) * 0.19 + t * 0.11) * Math.cos(along * 0.07 - t * 0.05);
  return smoothstepN(0.55, 1.0, b) * smoothstepN(0.3, 0.75, gate);
}
